import React from 'react';
import { Box, Stack, Tooltip as MuiTooltip, Typography } from '@mui/material';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Funnel,
  FunnelChart,
  LabelList,
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
  ZAxis,
} from 'recharts';
import { VizTooltip } from './ChartFrame';
import {
  AXIS_TICK,
  DIVERGING,
  INK,
  MARK,
  MUTED_MARK,
  ORDINAL_5,
  SERIES,
  STATUS,
  sequentialStep,
  inkOnSequential,
} from './vizTokens';

const GRID = { stroke: INK.grid, strokeWidth: 1 };

// ---------------------------------------------------------------------------
// Calibration plot
// ---------------------------------------------------------------------------
// The headline DCC chart. Stated understanding against graded take-home
// performance, with the line of perfect agreement drawn in.
//
// This replaces a bar chart that encoded understanding as bar length and left
// the take-home score as text inside the label — which meant the relationship
// between the two, the only thing worth knowing, was not encoded at all. A
// learner at 90% understanding and 8% take-home rendered as the longest bar on
// the chart. Here they land in the bottom-right corner, far below the diagonal,
// which is exactly where "believes they understand this, cannot do it" belongs.

export const CalibrationPlot = ({ points = [], band = 15, height = 380 }) => {
  const scored = points.filter((point) => point.understanding != null && point.takeHome != null);

  const renderTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const point = payload[0].payload;
    return (
      <VizTooltip
        title={point.name}
        rows={[
          { label: 'Stated understanding', value: `${point.understanding}%` },
          { label: 'Graded take-home', value: `${point.takeHome}%` },
          { label: 'Gap', value: `${point.calibrationGap > 0 ? '+' : ''}${point.calibrationGap} pts` },
        ]}
        note={point.calibration.label}
      />
    );
  };

  return (
    <ResponsiveContainer width="100%" height={height}>
      <ScatterChart margin={{ top: 16, right: 28, bottom: 44, left: 8 }}>
        <CartesianGrid {...GRID} />

        {/* The corridor is diagonal, so it is bounded by two lines parallel to
            y = x rather than a rectangle. Inside it, the difference between
            stated and demonstrated is small enough to treat as noise. */}
        <ReferenceLine
          segment={[{ x: 0, y: band }, { x: 100 - band, y: 100 }]}
          stroke={INK.grid}
          strokeWidth={1}
          ifOverflow="hidden"
        />
        <ReferenceLine
          segment={[{ x: band, y: 0 }, { x: 100, y: 100 - band }]}
          stroke={INK.grid}
          strokeWidth={1}
          ifOverflow="hidden"
        />
        <ReferenceLine
          segment={[{ x: 0, y: 0 }, { x: 100, y: 100 }]}
          stroke={INK.axis}
          strokeWidth={MARK.strokeWidth}
          label={{
            value: 'understanding = performance',
            position: 'insideBottomRight',
            fill: INK.muted,
            fontSize: 11,
          }}
        />
        {/* Names the half of the plot that matters, so "below the line" does
            not have to be decoded from the axis titles. */}
        <ReferenceLine
          segment={[{ x: 62, y: 16 }, { x: 62, y: 16 }]}
          stroke="none"
          label={{
            value: 'believes they understand it',
            position: 'center',
            fill: INK.muted,
            fontSize: 10,
          }}
        />

        <XAxis
          type="number"
          dataKey="understanding"
          domain={[0, 100]}
          ticks={[0, 25, 50, 75, 100]}
          tick={AXIS_TICK}
          tickLine={false}
          axisLine={{ stroke: INK.axis }}
          label={{ value: 'Stated understanding (%)', position: 'insideBottom', offset: -28, fill: INK.secondary, fontSize: 12 }}
        />
        <YAxis
          type="number"
          dataKey="takeHome"
          domain={[0, 100]}
          ticks={[0, 25, 50, 75, 100]}
          tick={AXIS_TICK}
          tickLine={false}
          axisLine={{ stroke: INK.axis }}
          label={{ value: 'Graded take-home (%)', angle: -90, position: 'insideLeft', fill: INK.secondary, fontSize: 12, style: { textAnchor: 'middle' } }}
        />
        <ZAxis range={[140, 140]} />
        <RechartsTooltip content={renderTooltip} cursor={{ strokeDasharray: '0', stroke: INK.grid }} />

        <Scatter data={scored} isAnimationActive={false}>
          {scored.map((point) => (
            <Cell
              key={point.id}
              fill={point.calibration.key === 'calibrated' ? SERIES.blue : STATUS[point.calibration.status]}
              stroke={INK.surface}
              strokeWidth={MARK.dotRing}
            />
          ))}
          <LabelList
            dataKey="name"
            position="top"
            offset={10}
            style={{ fontSize: 11, fontWeight: 700, fill: INK.secondary }}
          />
        </Scatter>
      </ScatterChart>
    </ResponsiveContainer>
  );
};

// ---------------------------------------------------------------------------
// Delivery quadrant
// ---------------------------------------------------------------------------
// Completion rate against self-rated confidence, split at the cohort medians.
// Position is the encoding; the four corner labels name what each region means
// so nobody has to work out which corner is the bad one.

export const DeliveryQuadrant = ({ points = [], reference, height = 380 }) => {
  const plotted = points.filter((point) => point.completionRate != null);
  const xMid = reference.medianCompletion;
  const yMid = reference.medianConfidence;

  const zones = [
    { x1: 0, x2: xMid, y1: yMid, y2: 100, label: 'Confident, not finishing', status: 'serious' },
    { x1: xMid, x2: 100, y1: yMid, y2: 100, label: 'On track', status: 'good' },
    { x1: 0, x2: xMid, y1: 0, y2: yMid, label: 'Stalled', status: 'critical' },
    { x1: xMid, x2: 100, y1: 0, y2: yMid, label: 'Delivering, low confidence', status: 'warning' },
  ];

  const renderTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const point = payload[0].payload;
    return (
      <VizTooltip
        title={point.name}
        rows={[
          { label: 'Completion rate', value: `${point.completionRate}%` },
          { label: 'Lessons', value: `${point.lessonsCompleted}/${point.lessonsStarted}` },
          { label: 'Confidence', value: `${point.confidence}%` },
          { label: 'Evidence saved', value: point.evidence },
        ]}
        note={point.quadrant?.label}
      />
    );
  };

  return (
    <ResponsiveContainer width="100%" height={height}>
      <ScatterChart margin={{ top: 16, right: 28, bottom: 44, left: 8 }}>
        <CartesianGrid {...GRID} />
        {zones.map((zone) => (
          <ReferenceArea
            key={zone.label}
            x1={zone.x1}
            x2={zone.x2}
            y1={zone.y1}
            y2={zone.y2}
            fill={STATUS[zone.status]}
            fillOpacity={0.05}
            stroke="none"
            label={{ value: zone.label, position: 'insideTopLeft', fill: INK.muted, fontSize: 10, offset: 8 }}
          />
        ))}
        <ReferenceLine x={xMid} stroke={INK.axis} strokeWidth={1} />
        <ReferenceLine y={yMid} stroke={INK.axis} strokeWidth={1} />

        <XAxis
          type="number"
          dataKey="completionRate"
          domain={[0, 100]}
          ticks={[0, 25, 50, 75, 100]}
          tick={AXIS_TICK}
          tickLine={false}
          axisLine={{ stroke: INK.axis }}
          label={{ value: `Completion rate (%) — cohort median ${xMid}%`, position: 'insideBottom', offset: -28, fill: INK.secondary, fontSize: 12 }}
        />
        <YAxis
          type="number"
          dataKey="confidence"
          domain={[0, 100]}
          ticks={[0, 25, 50, 75, 100]}
          tick={AXIS_TICK}
          tickLine={false}
          axisLine={{ stroke: INK.axis }}
          label={{ value: 'Self-rated confidence (%)', angle: -90, position: 'insideLeft', fill: INK.secondary, fontSize: 12, style: { textAnchor: 'middle' } }}
        />
        <ZAxis dataKey="evidence" range={[90, 320]} />
        <RechartsTooltip content={renderTooltip} cursor={{ strokeDasharray: '0', stroke: INK.grid }} />

        <Scatter data={plotted} isAnimationActive={false}>
          {plotted.map((point) => (
            <Cell
              key={point.id}
              fill={point.quadrant?.status === 'good' ? SERIES.blue : STATUS[point.quadrant?.status] || MUTED_MARK}
              fillOpacity={0.85}
              stroke={INK.surface}
              strokeWidth={MARK.dotRing}
            />
          ))}
          <LabelList
            dataKey="name"
            position="top"
            offset={12}
            style={{ fontSize: 11, fontWeight: 700, fill: INK.secondary }}
          />
        </Scatter>
      </ScatterChart>
    </ResponsiveContainer>
  );
};

// ---------------------------------------------------------------------------
// Gap bar — value against a cohort reference
// ---------------------------------------------------------------------------
// Sorted, and drawn as distance from the cohort median rather than as a raw
// count. This is the fix for the bug where a learner at 14/15 rendered as the
// shortest bar in the cohort despite having the highest completion rate: bar
// length now encodes the rate, and colour encodes which side of the median it
// falls on.

export const GapBarChart = ({ rows = [], referenceValue = 0, referenceLabel = 'cohort median', unit = '%', height }) => {
  const sorted = [...rows].sort((a, b) => (b.value ?? -1) - (a.value ?? -1));
  const chartHeight = height || Math.max(200, sorted.length * 40 + 56);

  const renderTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const row = payload[0].payload;
    if (row.value == null) return <VizTooltip title={row.name} note={row.detail} />;
    const delta = row.value - referenceValue;
    return (
      <VizTooltip
        title={row.name}
        rows={[
          { label: 'Value', value: `${row.value}${unit}` },
          { label: `vs ${referenceLabel}`, value: `${delta > 0 ? '+' : ''}${delta}${unit}` },
        ]}
        note={row.detail}
      />
    );
  };

  return (
    <ResponsiveContainer width="100%" height={chartHeight}>
      <BarChart data={sorted} layout="vertical" margin={{ top: 4, right: 64, left: 8, bottom: 28 }}>
        <CartesianGrid {...GRID} horizontal={false} />
        <XAxis
          type="number"
          domain={[0, 100]}
          ticks={[0, 25, 50, 75, 100]}
          tick={AXIS_TICK}
          tickLine={false}
          axisLine={{ stroke: INK.axis }}
        />
        <YAxis
          type="category"
          dataKey="name"
          width={150}
          tick={{ ...AXIS_TICK, fill: INK.secondary, fontWeight: 700 }}
          tickLine={false}
          axisLine={false}
        />
        <RechartsTooltip content={renderTooltip} cursor={{ fill: INK.plane }} />
        <ReferenceLine
          x={referenceValue}
          stroke={INK.primary}
          strokeWidth={MARK.strokeWidth}
          label={{ value: referenceLabel, position: 'top', fill: INK.secondary, fontSize: 11, fontWeight: 700 }}
        />
        <Bar dataKey="value" radius={[0, MARK.barRadius, MARK.barRadius, 0]} barSize={MARK.barSize} isAnimationActive={false}>
          {sorted.map((row) => (
            <Cell
              key={row.name}
              fill={row.value == null ? MUTED_MARK : row.value >= referenceValue ? DIVERGING.positive : DIVERGING.negative}
            />
          ))}
          <LabelList
            dataKey="label"
            position="right"
            offset={8}
            style={{ fontSize: 11, fontWeight: 700, fill: INK.secondary }}
          />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

// ---------------------------------------------------------------------------
// Progress funnel
// ---------------------------------------------------------------------------
// Stages are nested subsets, so the shape can only ever narrow. The previous
// funnel counted learners in some stages and take-home submissions in others,
// which produced a stage wider than the population it was drawn from.

export const ProgressFunnel = ({ stages = [], height = 300 }) => {
  // Label sits outside the funnel body carrying both name and count. Inside
  // labels would need ink that flips partway down the ramp — the opening
  // stages are light enough that white text on them fails contrast.
  const data = stages.map((stage, index) => ({
    ...stage,
    display: `${stage.label} · ${stage.value}`,
    fill: ORDINAL_5[Math.min(index, ORDINAL_5.length - 1)],
  }));

  const renderTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const stage = payload[0].payload;
    const total = stages[0]?.value || 1;
    return (
      <VizTooltip
        title={stage.label}
        rows={[
          { label: 'Learners', value: stage.value },
          { label: 'Of cohort', value: `${Math.round((stage.value / total) * 100)}%` },
        ]}
        note={stage.members?.map((member) => member.name).join(', ')}
      />
    );
  };

  return (
    <ResponsiveContainer width="100%" height={height}>
      {/* The right margin is reserved for the stage labels: without it the
          widest stage spans the full plot and its label is cut off. */}
      <FunnelChart margin={{ top: 8, right: 168, bottom: 8, left: 4 }}>
        <RechartsTooltip content={renderTooltip} />
        <Funnel dataKey="value" data={data} isAnimationActive={false} stroke={INK.surface} strokeWidth={MARK.gap}>
          <LabelList
            position="right"
            dataKey="display"
            style={{ fontSize: 12, fontWeight: 700, fill: INK.secondary }}
          />
        </Funnel>
      </FunnelChart>
    </ResponsiveContainer>
  );
};

// ---------------------------------------------------------------------------
// Lens mastery heatmap
// ---------------------------------------------------------------------------
// Learner x rubric lens, cell shaded by proportion correct. Replaces roughly
// five hundred individually-coloured chips that could not be scanned for a
// pattern. Built as a CSS grid rather than an SVG chart so the cells stay
// selectable text and the values are readable without a tooltip.

export const LensHeatmap = ({ matrix, height }) => {
  const { lenses = [], learners = [] } = matrix || {};
  if (!lenses.length || !learners.length) return null;

  const cellLabel = (cell) => (cell.ratio == null ? '—' : `${Math.round(cell.ratio * 100)}%`);

  return (
    <Box sx={{ overflowX: 'auto', height, pb: 1 }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: `minmax(140px, 1.4fr) repeat(${lenses.length}, minmax(58px, 1fr))`,
          gap: `${MARK.gap}px`,
          minWidth: 140 + lenses.length * 62,
        }}
      >
        <Box />
        {lenses.map((lens) => (
          <MuiTooltip key={lens.key} title={lens.label}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-end',
                justifyContent: 'center',
                pb: 0.75,
                minHeight: 76,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  writingMode: 'vertical-rl',
                  transform: 'rotate(180deg)',
                  color: INK.secondary,
                  fontWeight: 700,
                  fontSize: 10.5,
                  lineHeight: 1.2,
                  maxHeight: 72,
                  overflow: 'hidden',
                }}
              >
                {lens.label}
              </Typography>
            </Box>
          </MuiTooltip>
        ))}

        {learners.map((learner) => (
          <React.Fragment key={learner.id || learner.name}>
            <Box sx={{ display: 'flex', alignItems: 'center', pr: 1, minWidth: 0 }}>
              <Typography
                variant="caption"
                noWrap
                sx={{ fontWeight: 700, color: INK.secondary }}
                title={learner.name}
              >
                {learner.name}
              </Typography>
            </Box>
            {learner.cells.map((cell) => {
              const empty = cell.ratio == null;
              return (
                <MuiTooltip
                  key={`${learner.id}-${cell.lensKey}`}
                  title={`${learner.name} — ${cell.lensLabel}: ${empty ? 'not graded' : `${Math.round(cell.ratio * 100)}% across ${cell.graded} table${cell.graded === 1 ? '' : 's'}`}`}
                >
                  <Box
                    sx={{
                      minHeight: 38,
                      borderRadius: 1,
                      display: 'grid',
                      placeItems: 'center',
                      bgcolor: empty ? INK.plane : sequentialStep(cell.ratio),
                      border: empty ? `1px dashed ${INK.axis}` : 'none',
                      cursor: 'default',
                    }}
                  >
                    {/* The number is present in every cell, so the colour
                        scale is reinforcement rather than the only channel. */}
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 800,
                        fontSize: 11,
                        fontVariantNumeric: 'tabular-nums',
                        color: empty ? INK.muted : inkOnSequential(cell.ratio),
                      }}
                    >
                      {cellLabel(cell)}
                    </Typography>
                  </Box>
                </MuiTooltip>
              );
            })}
          </React.Fragment>
        ))}
      </Box>

      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }}>
        <Typography variant="caption" sx={{ color: INK.muted, fontWeight: 700 }}>0%</Typography>
        <Box
          sx={{
            flexGrow: 1,
            maxWidth: 180,
            height: 8,
            borderRadius: 999,
            background: `linear-gradient(90deg, ${sequentialStep(0)}, ${sequentialStep(0.5)}, ${sequentialStep(1)})`,
          }}
        />
        <Typography variant="caption" sx={{ color: INK.muted, fontWeight: 700 }}>100% correct</Typography>
        <Box sx={{ width: 14, height: 14, ml: 1.5, borderRadius: 0.5, border: `1px dashed ${INK.axis}`, bgcolor: INK.plane }} />
        <Typography variant="caption" sx={{ color: INK.muted }}>not graded</Typography>
      </Stack>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Ranked lens weakness
// ---------------------------------------------------------------------------
// Cohort rollup, sorted weakest-first. One accent on the weakest few, grey for
// the rest — the chart is answering "what goes on Monday's board", so only the
// bottom of the list needs emphasis.

export const LensRankBar = ({ lenses = [], highlightCount = 3, height }) => {
  const sorted = [...lenses]
    .filter((lens) => lens.ratio != null)
    .sort((a, b) => a.ratio - b.ratio)
    .map((lens, index) => ({
      ...lens,
      percent: Math.round(lens.ratio * 100),
      highlighted: index < highlightCount,
    }));
  const chartHeight = height || Math.max(200, sorted.length * 34 + 48);

  const renderTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const lens = payload[0].payload;
    return (
      <VizTooltip
        title={lens.label}
        rows={[
          { label: 'Cohort correct', value: `${lens.percent}%` },
          { label: 'Learners graded', value: lens.covered },
        ]}
      />
    );
  };

  return (
    <ResponsiveContainer width="100%" height={chartHeight}>
      <BarChart data={sorted} layout="vertical" margin={{ top: 4, right: 48, left: 8, bottom: 24 }}>
        <CartesianGrid {...GRID} horizontal={false} />
        <XAxis
          type="number"
          domain={[0, 100]}
          ticks={[0, 25, 50, 75, 100]}
          tick={AXIS_TICK}
          tickLine={false}
          axisLine={{ stroke: INK.axis }}
        />
        <YAxis
          type="category"
          dataKey="label"
          width={160}
          tick={{ ...AXIS_TICK, fill: INK.secondary, fontWeight: 700 }}
          tickLine={false}
          axisLine={false}
        />
        <RechartsTooltip content={renderTooltip} cursor={{ fill: INK.plane }} />
        <Bar dataKey="percent" radius={[0, MARK.barRadius, MARK.barRadius, 0]} barSize={MARK.barSize} isAnimationActive={false}>
          {sorted.map((lens) => (
            <Cell key={lens.key} fill={lens.highlighted ? STATUS.critical : MUTED_MARK} />
          ))}
          <LabelList
            dataKey="percent"
            position="right"
            offset={8}
            formatter={(value) => `${value}%`}
            style={{ fontSize: 11, fontWeight: 700, fill: INK.secondary }}
          />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

// ---------------------------------------------------------------------------
// Rubric radar
// ---------------------------------------------------------------------------
// One learner's rubric profile against the cohort average. Two series, so the
// legend is present; the shape comparison is the point, which a pair of bar
// charts would not give.

export const RubricRadar = ({ axes = [], learnerName: name, height = 320 }) => {
  if (axes.length < 3) return null;
  return (
    <ResponsiveContainer width="100%" height={height}>
      <RadarChart data={axes} margin={{ top: 16, right: 32, bottom: 16, left: 32 }} outerRadius="72%">
        <PolarGrid stroke={INK.grid} />
        <PolarAngleAxis dataKey="label" tick={{ ...AXIS_TICK, fontSize: 11, fill: INK.secondary }} />
        <PolarRadiusAxis domain={[0, 100]} tick={{ ...AXIS_TICK, fontSize: 10 }} axisLine={false} tickCount={5} />
        <RechartsTooltip
          content={({ active, payload, label }) => {
            if (!active || !payload?.length) return null;
            return (
              <VizTooltip
                title={label}
                rows={payload.map((entry) => ({ label: entry.name, value: `${Math.round(entry.value)}%` }))}
              />
            );
          }}
        />
        <Radar
          name="Cohort average"
          dataKey="cohort"
          stroke={MUTED_MARK}
          strokeWidth={MARK.strokeWidth}
          fill={MUTED_MARK}
          fillOpacity={0.3}
          isAnimationActive={false}
        />
        <Radar
          name={name}
          dataKey="learner"
          stroke={SERIES.blue}
          strokeWidth={MARK.strokeWidth}
          fill={SERIES.blue}
          fillOpacity={0.22}
          isAnimationActive={false}
        />
      </RadarChart>
    </ResponsiveContainer>
  );
};

// ---------------------------------------------------------------------------
// Coverage bar — how much of the cohort has each evidence type
// ---------------------------------------------------------------------------
// A 100% stacked composition. Answers "how much of this cohort have I actually
// got evidence for", which the KPI tiles ("21 clinic records") never did:
// twenty-one records could be one prolific learner and four silent ones.

export const CoverageBar = ({ segments = [], total = 0, height = 132 }) => {
  const data = [segments.reduce((acc, segment) => ({ ...acc, [segment.key]: segment.value }), { name: 'coverage' })];
  const palette = [SERIES.blue, ORDINAL_5[0], MUTED_MARK];

  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} layout="vertical" margin={{ top: 8, right: 8, left: 8, bottom: 32 }} barCategoryGap={0}>
        <XAxis type="number" domain={[0, total || 1]} hide />
        <YAxis type="category" dataKey="name" hide />
        <RechartsTooltip
          cursor={false}
          content={({ active, payload }) => {
            if (!active || !payload?.length) return null;
            return (
              <VizTooltip
                title="Evidence coverage"
                rows={payload.map((entry) => {
                  const segment = segments.find((item) => item.key === entry.dataKey);
                  return {
                    label: segment?.label || entry.dataKey,
                    value: `${entry.value} of ${total}`,
                  };
                })}
              />
            );
          }}
        />
        {segments.map((segment, index) => (
          <Bar
            key={segment.key}
            dataKey={segment.key}
            stackId="coverage"
            fill={palette[index] || MUTED_MARK}
            stroke={INK.surface}
            strokeWidth={MARK.gap}
            radius={index === 0 ? [MARK.barRadius, 0, 0, MARK.barRadius] : index === segments.length - 1 ? [0, MARK.barRadius, MARK.barRadius, 0] : 0}
            barSize={44}
            isAnimationActive={false}
          >
            <LabelList
              dataKey={segment.key}
              position="center"
              // Only label a segment wide enough to hold the text — a clipped
              // number is worse than no number, and the tooltip still has it.
              formatter={(value) => (total && value / total >= 0.12 ? value : '')}
              style={{ fontSize: 12, fontWeight: 800, fill: index === 2 ? INK.secondary : '#ffffff' }}
            />
          </Bar>
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
};

// A legend for CoverageBar, rendered outside the plot so the segments stay
// identifiable when a segment is too thin to carry its own label.
export const CoverageLegend = ({ segments = [] }) => {
  const palette = [SERIES.blue, ORDINAL_5[0], MUTED_MARK];
  return (
    <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
      {segments.map((segment, index) => (
        <Stack key={segment.key} direction="row" spacing={0.75} alignItems="center">
          <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: palette[index] || MUTED_MARK, flexShrink: 0 }} />
          <Typography variant="caption" sx={{ color: INK.secondary, fontWeight: 600 }}>
            {segment.label} ({segment.value})
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
};

// ---------------------------------------------------------------------------
// Pipeline bar — ordered stages, not a value ranking
// ---------------------------------------------------------------------------
// Hackathon role status. Stages keep their workflow order rather than sorting
// by value, because the sequence is the meaning; the ordinal ramp reinforces
// direction of travel.

export const PipelineBar = ({ stages = [], height = 260 }) => {
  const renderTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const stage = payload[0].payload;
    const total = stages.reduce((sum, item) => sum + item.value, 0) || 1;
    return (
      <VizTooltip
        title={stage.label}
        rows={[
          { label: 'Roles', value: stage.value },
          { label: 'Of all roles', value: `${Math.round((stage.value / total) * 100)}%` },
        ]}
      />
    );
  };

  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={stages} margin={{ top: 20, right: 12, left: 8, bottom: 28 }}>
        <CartesianGrid {...GRID} vertical={false} />
        <XAxis
          dataKey="label"
          tick={{ ...AXIS_TICK, fill: INK.secondary, fontWeight: 700 }}
          tickLine={false}
          axisLine={{ stroke: INK.axis }}
          interval={0}
        />
        <YAxis allowDecimals={false} tick={AXIS_TICK} tickLine={false} axisLine={false} />
        <RechartsTooltip content={renderTooltip} cursor={{ fill: INK.plane }} />
        <Bar dataKey="value" radius={[MARK.barRadius, MARK.barRadius, 0, 0]} barSize={46} isAnimationActive={false}>
          {stages.map((stage, index) => (
            <Cell key={stage.key} fill={ORDINAL_5[Math.min(index, ORDINAL_5.length - 1)]} />
          ))}
          <LabelList
            dataKey="value"
            position="top"
            offset={6}
            style={{ fontSize: 12, fontWeight: 800, fill: INK.secondary }}
          />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};
